import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLang, useTextDomainContext, useUser } from "@/app/providers";
import {
  GENERATE_COST_STATEMENT_FILE,
  useGetGroups,
  useBudgetData,
} from "@/hooks";

import {
  FilterBar1,
  AreaChartBox,
  LineChartBox,
  BarChartBox,
  TableView1,
  ToolbarDropdown,
} from "@/components";
import moment from "moment";
import {
  exportableStatements,
  formatDateBySegment,
  formatDateForAPI,
  meterApiDataToBarChartData,
  segmentList,
  toLocaleNumber,
  urlFriendlyString,
} from "@/utils";
import { useTheme } from "@mui/material";
import { isEqual } from "lodash";
import { TfiImport } from "react-icons/tfi";
import { enqueueSnackbar } from "notistack";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { queries } from "@/graphql";

export function Budget() {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  // Fetch budget-chart-type settings with user -> tenant -> global fallback
  const { data: userSettingData } = useQuery(queries.userSettings, {
    variables: { key: "budget-chart-type" },
  });

  const { data: tenantSettingData } = useQuery(queries.tenantSettings, {
    variables: { key: "budget-chart-type" },
  });

  const { data: globalSettingData } = useQuery(queries.getSettings, {
    variables: { key: "budget-chart-type" },
  });

  const activeChartType = useMemo(() => {
    const extractVal = (raw: any): string | null => {
      if (!raw) return null;
      let parsed: any = raw;
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "userSettings" in parsed
      ) {
        parsed = parsed.userSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "userSettings" in parsed
      ) {
        parsed = parsed.userSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "tenantSettings" in parsed
      ) {
        parsed = parsed.tenantSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "settings" in parsed
      ) {
        parsed = parsed.settings;
      }
      return typeof parsed === "string" ? parsed : null;
    };

    const userVal = extractVal(userSettingData);
    if (userVal && userVal !== "Default") {
      return userVal;
    }

    const tenantVal = extractVal(tenantSettingData);
    if (tenantVal && tenantVal !== "Default") {
      return tenantVal;
    }

    const globalVal = extractVal(globalSettingData);
    if (globalVal && globalVal !== "Default") {
      return globalVal;
    }

    return "Bar Chart";
  }, [userSettingData, tenantSettingData, globalSettingData]);

  const SHOW_AREA_CHART = activeChartType === "Area Chart";
  const SHOW_LINE_CHART = activeChartType === "Line Chart";
  const SHOW_BAR_CHART = activeChartType === "Bar Chart";
  const { user } = useUser();
  const isSuperAdmin = user?.roles?.includes("super-admin");
  const segments = segmentList();
  const theme = useTheme();
  const locale = useLang();

  type SegmentKey = keyof ReturnType<typeof segmentList>;

  interface SelectedValuesPayload {
    building?: { value?: string };
    meter?: { value?: string };
    energyType?: { value?: string };
    date?: { startDate?: Date; endDate?: Date };
    segment?: SegmentKey;
    shortSegment?: { value?: string };
    country?: string;
  }

  let {
    currentTeam: customer,
    currentSubsidiary: group,
    currentTeamId,
    currentSubsidiaryId,
  } = user || {};

  if (!customer) {
    customer = "all";
  }

  if (!group) {
    group = "All";
  }

  // State
  const [building, setBuilding] = useState("all");
  const [meter, setMeter] = useState("all");
  const [energyType, setEnergyType] = useState("electricity");
  const [fromDate, setFromDate] = useState<string | undefined>(undefined);
  const [toDate, setToDate] = useState<string | undefined>(undefined);
  const [segment, setSegment] = useState("year");
  const [shortSegment, setShortSegment] = useState("m");
  const [fileNameForExport, setFileNameForExport] = useState("budget-export");
  const [chartData, setChartData] = useState<any[]>([]);
  const [exportData, setExportData] = useState<any[]>([]);
  const [country, setCountry] = useState<string | null>(null);

  const isGas = useMemo(() => {
    const t = (
      typeof energyType === "string"
        ? energyType
        : (energyType as any)?.value || ""
    )
      .toLowerCase()
      .trim();
    return t === "gas" || t === "natural gas";
  }, [energyType]);

  const filters = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: formatDateForAPI(fromDate),
    toDate: formatDateForAPI(toDate),
    resolution: shortSegment,
    country,
  };

  const { data: budgetDataList, loading } = useBudgetData(filters);

  const [addExcelInQueue] = useLazyQuery(GENERATE_COST_STATEMENT_FILE);

  const { subsidiary } = useGetGroups({
    customerId: currentTeamId,
    customerName: customer,
  });

  useEffect(() => {
    setFileNameForExport(
      urlFriendlyString(
        "budget-" +
          building +
          "-" +
          meter +
          "-" +
          energyType +
          "-" +
          segment +
          "-" +
          fromDate +
          "-" +
          toDate,
      ),
    );
  }, [building, energyType, fromDate, meter, segment, toDate]);

  useEffect(() => {
    if (budgetDataList.length) {
      const chartDateTemp = meterApiDataToBarChartData(
        budgetDataList,
        shortSegment,
      );
      if (!isEqual(chartDateTemp, chartData)) {
        setChartData(meterApiDataToBarChartData(budgetDataList, shortSegment));
      }
    }
  }, [chartData, budgetDataList, shortSegment]);

  useEffect(() => {
    const fetchData = async () => {
      const customerName = group.toLowerCase() === "all" ? customer : group;
      let fileNamePostfix = group.toLowerCase() === "all" ? "-all" : "";
      if (subsidiary.length < 2) {
        fileNamePostfix = "-all";
      }
      const res = [];

      const res1 = await exportableStatements(
        "cost-statement",
        customerName,
        fileNamePostfix,
        energyType,
        fromDate,
        gettext,
      );

      if (isSuperAdmin) {
        res.push(...res1);
        const obj = {
          label: gettext("Generate Budget Report"),
          key: "generate-report",
        };
        res.push(obj);
      } else {
        res.push(...res1);
      }

      setExportData(res);
    };
    fetchData().then();
  }, [
    customer,
    energyType,
    fromDate,
    gettext,
    group,
    isSuperAdmin,
    subsidiary.length,
  ]);

  const columns = useMemo(
    () => [
      {
        field: "ts",
        headerName: gettext("Date"),
        flex: 1,
        type: "date",
        valueGetter: (value: string | undefined) => {
          let returnValue = "";
          if (value) {
            if (value === "Summary") {
              returnValue = value;
            } else {
              returnValue = value.split("T")[0];
            }
          }
          return returnValue;
        },
        valueFormatter: (value: string | undefined) => {
          let returnValue = "";
          if (value) {
            if (value === "Summary") {
              returnValue = value;
            } else {
              returnValue = formatDateBySegment(new Date(value), segment);
            }
          }
          return returnValue;
        },
      },
      {
        field: isGas ? "consumptionVM3" : "consumptionV",
        headerName: isGas
          ? gettext("Consumption (m³)")
          : gettext("Consumption (kWh)"),
        flex: 1,
        type: "number",
        valueGetter: (_value: any, row: any) => {
          if (!row) return _value;
          if (isGas) {
            return row.consumptionVM3 !== undefined &&
              row.consumptionVM3 !== null
              ? row.consumptionVM3
              : row.consumptionV !== undefined && row.consumptionV !== null
                ? row.consumptionV / 12.157
                : _value;
          }
          return row.consumptionV ?? _value;
        },
        valueFormatter: (value: number | string | undefined) =>
          toLocaleNumber(locale, value, 2),
      },
      {
        field: isGas ? "consumptionPriceM3" : "consumptionPrice",
        headerName: isGas
          ? gettext("Price (DKK/m³)")
          : gettext("Price (Øre/kWh)"),
        flex: 1,
        type: "number",
        valueGetter: (_value: any, row: any) => {
          if (!row) return _value;
          if (isGas) {
            return row.consumptionPriceM3 ?? row.consumptionPrice ?? _value;
          }
          return row.consumptionPrice ?? _value;
        },
        valueFormatter: (value: number | string | undefined) =>
          toLocaleNumber(locale, value, 2),
      },
      {
        field: isGas ? "consumptionTaxRateM3" : "consumptionTaxRate",
        headerName: isGas
          ? gettext("Taxes (DKK/m³)")
          : gettext("Taxes (Øre/kWh)"),
        flex: 1,
        type: "number",
        valueGetter: (_value: any, row: any) => {
          if (!row) return _value;
          if (isGas) {
            return (
              row.consumptionTaxRateM3 ??
              row.consumptionAggregatedTaxM3 ??
              row.consumptionTaxRate ??
              _value
            );
          }
          return row.consumptionTaxRate ?? _value;
        },
        valueFormatter: (value: number | string | undefined) =>
          toLocaleNumber(locale, value, 2),
      },
      {
        field: isGas ? "consumptionSupplierRateM3" : "consumptionSupplierRate",
        headerName: isGas
          ? gettext("Supplier (DKK/m³)")
          : gettext("Supplier (Øre/kWh)"),
        flex: 1,
        type: "number",
        valueGetter: (_value: any, row: any) => {
          if (!row) return _value;
          if (isGas) {
            return (
              row.consumptionSupplierRateM3 ??
              row.consumptionSupplierRate ??
              _value
            );
          }
          return row.consumptionSupplierRate ?? _value;
        },
        valueFormatter: (value: number | string | undefined) =>
          toLocaleNumber(locale, value, 2),
      },
      {
        field: isGas
          ? "consumptionNetselskabRateM3"
          : "consumptionNetselskabRate",
        headerName: isGas
          ? gettext("Grid (DKK/m³)")
          : gettext("Grid (Øre/kWh)"),
        flex: 1,
        type: "number",
        valueGetter: (_value: any, row: any) => {
          if (!row) return _value;
          if (isGas) {
            return (
              row.consumptionNetselskabRateM3 ??
              row.consumptionNetselskabRate ??
              _value
            );
          }
          return row.consumptionNetselskabRate ?? _value;
        },
        valueFormatter: (value: number | string | undefined) =>
          toLocaleNumber(locale, value, 2),
      },
      {
        field: "consumptionCost",
        headerName: gettext("Cost (DKK)"),
        flex: 1,
        type: "number",
        valueFormatter: (value: number | string | undefined) =>
          toLocaleNumber(locale, value, 2),
      },
    ],
    [gettext, isGas, locale, segment],
  );

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
  };

  const postToolbar = () => {
    return (
      <ToolbarDropdown
        data={exportData}
        label={gettext("Reports")}
        onClick={handleClickCustomDropdownOption}
        endIcon={<TfiImport size={14} />}
      />
    );
  };

  const handleGetSelectedValues = (selectedValues: SelectedValuesPayload) => {
    if (selectedValues?.building?.value) {
      setBuilding(selectedValues.building.value);
    }

    if (selectedValues?.meter?.value) {
      setMeter(selectedValues.meter.value);
    }

    if (selectedValues?.energyType) {
      const val =
        typeof selectedValues.energyType === "object"
          ? selectedValues.energyType.value ||
            (selectedValues.energyType as any).label
          : selectedValues.energyType;
      if (val) setEnergyType(val);
    }

    if (selectedValues?.date?.startDate) {
      setFromDate(selectedValues.date.startDate.toISOString());
    }

    if (selectedValues?.date?.endDate) {
      setToDate(selectedValues.date.endDate.toISOString());
    }

    if (selectedValues?.segment) {
      setSegment(selectedValues.segment);
      setShortSegment(segments[selectedValues.segment].shortSegment);
      if (
        selectedValues.segment === "day" &&
        moment(selectedValues?.date?.startDate).format("YYYY-MM-DD") !==
          moment(selectedValues?.date?.endDate).format("YYYY-MM-DD")
      ) {
        setSegment("month");
        setShortSegment(segments["month"].shortSegment);
      }
    }

    if (selectedValues?.shortSegment?.value) {
      setShortSegment(selectedValues.shortSegment.value);
    }

    if (selectedValues?.country) {
      setCountry(selectedValues.country);
    }
  };

  const handleClickCustomDropdownOption = (obj: { key: string }) => {
    let renamedGroup = group;
    if (subsidiary.length < 2) {
      renamedGroup = "all";
    }

    switch (obj.key) {
      case "generate-report":
        addExcelInQueue({
          variables: {
            meterType: energyType,
            customer: customer,
            group: renamedGroup,
            year: new Date(fromDate || "").getFullYear(),
          },
        }).then(
          ({ data }: { data: { generateCostStatementFile?: string } }) => {
            if (data?.generateCostStatementFile) {
              enqueueSnackbar(
                gettext(
                  "The system is now creating the report, and you can download it shortly by clicking Download.",
                ),
                { variant: "success" },
              );
            } else {
              enqueueSnackbar(
                gettext(
                  "An error occurred while the system was generating the report. Please try again. You can contact support If this continues.",
                ),
                { variant: "error" },
              );
            }
          },
        );
        break;
      default:
        break;
    }
  };

  return (
    <>
      <FilterBar1
        getSelectedValues={handleGetSelectedValues}
        currentTeam={customer}
        currentSubsidiary={group}
        currentTeamId={currentTeamId}
        currentSubsidiaryId={currentSubsidiaryId}
        showCountry
        showBuilding
        showMeter
        showEnergyType
        showDateSingle
        showDateSingleMonthly
        showBuildingAll
        showMeterAll
      />
      <br />

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Price in (DKK/m³)")
                : gettext("Price in (Øre/kWh)")
            }
            chartId={"price"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas ? "consumptionPriceM3" : "consumptionPrice",
                  label: gettext("Price"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Price in (DKK/m³)")
                : gettext("Price in (Øre/kWh)")
            }
            chartId={"price-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas ? "consumptionPriceM3" : "consumptionPrice",
                  label: gettext("Price"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Price in (DKK/m³)")
                : gettext("Price in (Øre/kWh)")
            }
            chartId={"price-bar"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas ? "consumptionPriceM3" : "consumptionPrice",
                  label: gettext("Price"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Taxes in (DKK/m³)")
                : gettext("Taxes in (Øre/kWh)")
            }
            chartId={"tax"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionTaxRateM3"
                    : "consumptionTaxRate",
                  label: gettext("Tax"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Taxes in (DKK/m³)")
                : gettext("Taxes in (Øre/kWh)")
            }
            chartId={"tax-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionTaxRateM3"
                    : "consumptionTaxRate",
                  label: gettext("Tax"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Taxes in (DKK/m³)")
                : gettext("Taxes in (Øre/kWh)")
            }
            chartId={"tax-bar"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionTaxRateM3"
                    : "consumptionTaxRate",
                  label: gettext("Tax"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Supplier in (DKK/m³)")
                : gettext("Supplier in (Øre/kWh)")
            }
            chartId={"supplier"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionSupplierRateM3"
                    : "consumptionSupplierRate",
                  label: gettext("Supplier"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Supplier in (DKK/m³)")
                : gettext("Supplier in (Øre/kWh)")
            }
            chartId={"supplier-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionSupplierRateM3"
                    : "consumptionSupplierRate",
                  label: gettext("Supplier"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Supplier in (DKK/m³)")
                : gettext("Supplier in (Øre/kWh)")
            }
            chartId={"supplier-bar"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionSupplierRateM3"
                    : "consumptionSupplierRate",
                  label: gettext("Supplier"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={loading}
            title={
              isGas ? gettext("Grid in (DKK/m³)") : gettext("Grid in (Øre/kWh)")
            }
            chartId={"grid"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionNetselskabRateM3"
                    : "consumptionNetselskabRate",
                  label: gettext("Grid"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={loading}
            title={
              isGas ? gettext("Grid in (DKK/m³)") : gettext("Grid in (Øre/kWh)")
            }
            chartId={"grid-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionNetselskabRateM3"
                    : "consumptionNetselskabRate",
                  label: gettext("Grid"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={loading}
            title={
              isGas ? gettext("Grid in (DKK/m³)") : gettext("Grid in (Øre/kWh)")
            }
            chartId={"grid-bar"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas
                    ? "consumptionNetselskabRateM3"
                    : "consumptionNetselskabRate",
                  label: gettext("Grid"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("(DKK/m³)") : gettext("(Øre/kWh)"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={loading}
            title={gettext("Cost in DKK")}
            chartId={"cost"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: "consumptionCost",
                  label: gettext("Cost"),
                  color: (theme.palette as any).chart.electricity,
                  unit: gettext("DKK"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={loading}
            title={gettext("Cost in DKK")}
            chartId={"cost-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: "consumptionCost",
                  label: gettext("Cost"),
                  color: (theme.palette as any).chart.electricity,
                  unit: gettext("DKK"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={loading}
            title={gettext("Cost in DKK")}
            chartId={"cost-bar"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: "consumptionCost",
                  label: gettext("Cost"),
                  color: (theme.palette as any).chart.electricity,
                  unit: gettext("DKK"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Consumption measured in m³")
                : gettext("Consumption measured in kWh")
            }
            chartId={"consumption"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas ? "consumptionVM3" : "consumptionV",
                  label: gettext("Consumption"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("m³") : gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Consumption measured in m³")
                : gettext("Consumption measured in kWh")
            }
            chartId={"consumption-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas ? "consumptionVM3" : "consumptionV",
                  label: gettext("Consumption"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("m³") : gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={loading}
            title={
              isGas
                ? gettext("Consumption measured in m³")
                : gettext("Consumption measured in kWh")
            }
            chartId={"consumption-bar"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: isGas ? "consumptionVM3" : "consumptionV",
                  label: gettext("Consumption"),
                  color: (theme.palette as any).chart.electricity,
                  unit: isGas ? gettext("m³") : gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}
      <br />

      <TableView1
        title={gettext("Budget data, prices, tariffs, taxes and cost")}
        rows={budgetDataList}
        columns={columns}
        hideFooterPagination
        loading={loading}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar
        fileNameForExport={fileNameForExport}
      />
    </>
  );
}
